import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

// Import retrieveViews so we can fetch the entire list for the project
import { retrieveViews } from "../services/database";

const RevisionHistory = ({ projectId, viewId, currentVersionId, onClose }) => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchViewData = async () => {
      try {
        // Fetch all views for the given project
        const allViews = await retrieveViews(projectId);
        // Find the single view matching "viewId"
        const matchedView = allViews.find((v) => v._id === viewId);

        if (!matchedView) {
          throw new Error("Could not find a view with the specified ID.");
        }

        setView(matchedView);
      } catch (err) {
        console.error("Error fetching view data:", err);
        setError("Could not load version history. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if projectId and viewId are provided
    if (projectId && viewId) {
      fetchViewData();
    } else {
      setError("Missing projectId or viewId.");
      setLoading(false);
    }
  }, [projectId, viewId]);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] flex items-center justify-center">
          <div className="text-xl">Loading version history...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
          <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center z-20">
            <h2 className="text-2xl font-bold">Version History</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="p-6 text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  const displayedVersions = (view?.versions || []).filter((ver) => {
    // List versions unless revisionStatus is explicitly 'error' or 'processing'
    return ver.revisionStatus !== 'error' && ver.revisionStatus !== 'processing';
  });

  // If the view has no completed versions, show a message
  if (!view || displayedVersions.length === 0) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
          <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center z-20">
            <h2 className="text-2xl font-bold">Version History</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="p-6 text-gray-500">No version history available.</div>
        </div>
      </div>
    );
  }

  // Sort completed versions by creation time (newest first)
  const sortedVersions = [...displayedVersions].sort((a, b) => {
    const timeA = new Date(a.time_created || 0);
    const timeB = new Date(b.time_created || 0);
    return timeB - timeA;
  });

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center z-20">
          <h2 className="text-2xl font-bold">Version History</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="overflow-y-auto p-4 flex-grow">
          <div className="space-y-4">
            {sortedVersions.map((version, index) => {
              // Compare version.versionId to currentVersionId
              const isCurrentVersion = version.versionId === currentVersionId;
              const versionNumber = sortedVersions.length - index;
              const formattedDate = version.time_created
                ? new Date(version.time_created).toLocaleString()
                : "Unknown date";

              return (
                <div
                  key={version.versionId}
                  className={`p-4 rounded-lg ${
                    isCurrentVersion
                      ? "border-2 border-black relative"
                      : "border border-gray-200"
                  }`}
                >
                  {isCurrentVersion && (
                    <span className="absolute top-2 right-2 bg-black text-white text-xs px-2 py-1 rounded">
                      Revising
                    </span>
                  )}

                  <h3 className="font-bold text-lg mb-1">
                    Version {versionNumber}
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      {formattedDate}
                    </span>
                  </h3>
                  <p className="text-gray-700">
                    {version.explanation || "No explanation available"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevisionHistory;
